// Facebook (+ linked Instagram) connection status hook.
//
// Pre-this-hook: every page that needed fbStatus (AppLayoutV2 for the
// sidebar Connect rows, gallery for the Post Now buttons, detail page
// for the publish picker, analytics for the empty-state, etc.) called
// getFacebookConnection() in its own useEffect on mount. Since each
// route mount runs AppLayoutV2 fresh, that meant a fresh /api/auth/
// facebook hit on EVERY navigation — Meta API ping per page click.
//
// Now there's a single context provider mounted above the router that
// fetches ONCE and caches the result. Consumers just read it.
//
// `refresh()` is exposed for the OAuth callback + Disconnect flows so
// the in-app state reflects the new connection without a page reload.
//
// Mounted in App.tsx inside AuthProvider (we key on user.id; signed-out
// users obviously don't have an FB connection).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  type FacebookConnectionStatus,
  getFacebookConnection,
} from '@/lib/api';
import { useAuth } from './useAuth';

interface FacebookConnectionContextType {
  status: FacebookConnectionStatus | null;
  loading: boolean;
  /** Re-fetch the connection status AND return the fresh result, so callers
   *  (e.g. the post-OAuth confirm toast) can read the just-linked Instagram
   *  account without waiting for the next render. */
  refresh: () => Promise<FacebookConnectionStatus | null>;
}

const FacebookConnectionContext =
  createContext<FacebookConnectionContextType | null>(null);

export function FacebookConnectionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  // Stable string key — see useCredits.tsx for why we key on user.id and
  // not the user object reference (avoids 3-5× re-fetches per page load
  // when supabase/firebase fires INITIAL_SESSION + SIGNED_IN + TOKEN_
  // REFRESHED events in sequence).
  const userId = user?.id ?? null;
  const [status, setStatus] = useState<FacebookConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (): Promise<FacebookConnectionStatus | null> => {
    if (!userId) {
      setStatus(null);
      setLoading(false);
      return null;
    }
    setLoading(true);
    try {
      const fresh = await getFacebookConnection();
      setStatus(fresh);
      return fresh;
    } catch {
      // Tolerate transient failures — consumers fall through to
      // not-connected UI, same as if the fetch returned {connected:false}.
      setStatus(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { void refresh(); }, [refresh]);

  return (
    <FacebookConnectionContext.Provider value={{ status, loading, refresh }}>
      {children}
    </FacebookConnectionContext.Provider>
  );
}

export function useFacebookConnection(): FacebookConnectionContextType {
  const ctx = useContext(FacebookConnectionContext);
  if (!ctx) {
    throw new Error(
      'useFacebookConnection must be used within FacebookConnectionProvider',
    );
  }
  return ctx;
}
